import React, { useState } from 'react';
import { Calendar, CheckCircle, AlertTriangle, Clock, Filter, Download } from 'lucide-react';
import TimelineVisualization from './components/TimelineVisualization';
import ValidationPanel from './components/ValidationPanel';
import MilestoneManagement from './components/MilestoneManagement';
import ComplianceScoring from './components/ComplianceScoring';
import CommunicationPanel from './components/CommunicationPanel';

const StatutoryComplianceTimelineValidationCenter = () => {
  const [activeView, setActiveView] = useState('monthly');
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [activeTab, setActiveTab] = useState('timeline');

  // Mock data for demonstration
  const stats = [
    { label: 'Total Milestones', value: '48', icon: Calendar, color: 'bg-blue-500' },
    { label: 'Completed', value: '32', icon: CheckCircle, color: 'bg-green-500' },
    { label: 'Pending', value: '12', icon: Clock, color: 'bg-yellow-500' },
    { label: 'Overdue', value: '4', icon: AlertTriangle, color: 'bg-red-500' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Statutory Compliance Timeline & Validation Center
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Comprehensive timeline visualization and data validation for regulatory compliance tracking
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                <Filter className="w-4 h-4" />
                Filter
              </button>
              <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                <Download className="w-4 h-4" />
                Export
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
            {stats?.map((stat, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">{stat?.label}</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">{stat?.value}</p>
                  </div>
                  <div className={`${stat?.color} p-3 rounded-lg`}>
                    <stat.icon className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Tab Navigation - Desktop */}
        <div className="hidden md:flex border-t border-gray-200 px-6">
          <button
            onClick={() => setActiveTab('timeline')}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'timeline' ?'border-blue-600 text-blue-600' :'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Timeline Visualization
          </button>
          <button
            onClick={() => setActiveTab('validation')}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'validation' ?'border-blue-600 text-blue-600' :'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Validation Panel
          </button>
          <button
            onClick={() => setActiveTab('milestones')}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'milestones' ?'border-blue-600 text-blue-600' :'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Milestone Management
          </button>
          <button
            onClick={() => setActiveTab('scoring')}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'scoring' ?'border-blue-600 text-blue-600' :'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Compliance Scoring
          </button>
          <button
            onClick={() => setActiveTab('communication')}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'communication' ?'border-blue-600 text-blue-600' :'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Communication
          </button>
        </div>

        {/* Tab Navigation - Mobile */}
        <div className="md:hidden px-4 py-3 border-t border-gray-200">
          <select
            value={activeTab}
            onChange={(e) => setActiveTab(e?.target?.value)}
            className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="timeline">Timeline Visualization</option>
            <option value="validation">Validation Panel</option>
            <option value="milestones">Milestone Management</option>
            <option value="scoring">Compliance Scoring</option>
            <option value="communication">Communication</option>
          </select>
        </div>
      </div>
      {/* Main Content */}
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          {activeTab === 'timeline' && (
            <TimelineVisualization
              activeView={activeView}
              setActiveView={setActiveView}
              onMilestoneSelect={setSelectedMilestone}
            />
          )}
          {activeTab === 'validation' && <ValidationPanel />}
          {activeTab === 'milestones' && (
            <MilestoneManagement
              selectedMilestone={selectedMilestone}
              onMilestoneSelect={setSelectedMilestone}
            />
          )}
          {activeTab === 'scoring' && <ComplianceScoring />}
          {activeTab === 'communication' && <CommunicationPanel />}
        </div>
      </div>
    </div>
  );
};

export default StatutoryComplianceTimelineValidationCenter;